// Admin invitations (arc 101 slice 01) — every invitation the platform minted,
// across kinds, and the two things an operator does to one: REVOKE a pending
// link, or RESEND it (rotate the token, mail a fresh link).
//
// The row is `InvitationView` from `@developerz/domain` — the projection every
// listing of an invitation serializes through, and the one that carries NO
// token: a list read that echoed the accept link would turn every read scope on
// this surface into a credential-harvesting read. The accept URL travels on two
// WRITE answers only, each shown once: the mint that provisioned the person
// (`lib/admin-access-requests.ts`) and a resend ({@link InvitationResent}),
// which re-arms the invitation and so kills the link the mint showed.
//
// Both writes are REST twins of the MCP `admin invites_revoke` /
// `admin invites_resend` actions (seat E),
// through one control — never a second door. The caller re-reads the list after
// each; nothing is patched from the echo. The answers are RETURNED, not dropped:
// a resend's link is the operator's only copy when the mail bounces, and a
// revoke that found the row already revoked (`revoked: false`) did not do what
// was asked (`AdminInvitationOutcome`).
//
// Reads follow `lib/adminOss.ts`: cursor-paged, the status filter ALWAYS sent in
// the shared `?filter=status:<value>` grammar, `all` a real value.

import type { InvitationStatus, InvitationView } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { apiFetch } from './api';
import { type OperatorOutcome, withOperatorCredential } from './operator-credential';
import type { Page } from './paged';
import { SESSION_TOKEN } from './session';

export type { InvitationStatus, InvitationView };

/** List filter — `all` drops the status fence; anything else pins one status. */
export type AdminInvitationFilter = InvitationStatus | 'all';

/** The filter vocabulary, in the route's own order (`pending|accepted|revoked|expired|all`). */
export const ADMIN_INVITATION_FILTERS: readonly AdminInvitationFilter[] = [
  'pending',
  'accepted',
  'revoked',
  'expired',
  'all',
];

export const ADMIN_INVITATIONS_KEY = 'admin_invitations';

const BASE = '/v1/admin/invitations';

/**
 * One page of invitations, newest-first. Idle (no fetch) with no session.
 *
 * Keyset-paged: the route fences the status in SQL BEFORE it cuts the page and
 * mints `next_cursor` (plan 101 A10 — it used to narrow a head of 100 in memory,
 * so 101 accepted rows hid every older pending one). `usePagedQuery` hands the
 * cursor back verbatim; `AdminInvitationsPanel.test.tsx` pins the round trip.
 */
export function useAdminInvitations(
  status: () => AdminInvitationFilter,
  cursor: () => string | null,
  perPage = 30,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<Page<InvitationView>>(() => ({
    queryKey: [ADMIN_INVITATIONS_KEY, token, status(), cursor(), perPage],
    enabled: token !== null,
    queryFn: () =>
      apiFetch<Page<InvitationView>>(`${BASE}?${listParams(status(), cursor(), perPage)}`, token),
  }));
}

/** `POST …/revoke`'s answer. `revoked: false` = it was ALREADY revoked, at `revoked_at_iso`. */
export type InvitationRevokeAnswer = {
  readonly invitation_id: string;
  readonly revoked: boolean;
  readonly revoked_at_iso: string;
};

/** `POST …/resend`'s answer: the fresh bearer link, shown ONCE (the route is `secret`). */
export type InvitationResent = {
  readonly invitation_id: string;
  readonly email: string;
  readonly accept_url: string;
  readonly expires_at_iso: string;
  readonly reissued: boolean;
  /** Whether the fresh mail LEFT. "On its way" is said only when true (deep sweep 03-13). */
  readonly mailed: boolean;
};

/** What one row write did, for the panel to say (`AdminInvitationOutcome`). */
export type InvitationOutcome =
  | { readonly kind: 'revoked'; readonly answer: InvitationRevokeAnswer }
  | { readonly kind: 'resent'; readonly answer: InvitationResent };

/**
 * Withdraw a pending invitation. Terminal — a revoked invitation is never
 * revived; a person who still needs one gets a fresh mint. The caller re-reads.
 */
export function revokeAdminInvitation(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorOutcome<InvitationRevokeAnswer>> {
  return invitationWrite<InvitationRevokeAnswer>(id, 'revoke', token);
}

/**
 * Rotate the token and mail a fresh link to the same address. The old link
 * stops working the moment this lands, so the answer's `accept_url` is the only
 * copy of the live one. The caller shows it, then re-reads.
 */
export function resendAdminInvitation(
  id: string,
  token: string | null = SESSION_TOKEN,
): Promise<OperatorOutcome<InvitationResent>> {
  return invitationWrite<InvitationResent>(id, 'resend', token);
}

/** Both verbs ride `admin:users`, on an operator credential (#5724). */
function invitationWrite<T>(
  id: string,
  verb: 'revoke' | 'resend',
  token: string | null,
): Promise<OperatorOutcome<T>> {
  return withOperatorCredential(
    { scopes: ['admin:users'], name: `admin console: ${verb} an invitation`, token },
    (send) => send<T>(`${BASE}/${encodeURIComponent(id)}/${verb}`, { method: 'POST' }),
  );
}

/** `?filter=status:<value>&per_page=&cursor=` — `status` always sent (see header). */
function listParams(status: AdminInvitationFilter, cursor: string | null, perPage: number): string {
  const params = new URLSearchParams({ filter: `status:${status}`, per_page: String(perPage) });
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}
